import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import i18n from '_/i18n'
import { eventBus } from '_/helpers/eventBus'
import { getEntryFields } from '_/components/Admin/Admin.fields'
import adminFactory from '_/vuex/modules/admin'
import { store } from '_/vuex/store'
import { pLog, pWarn, pError } from '_/helpers/logger'
import { SchedulePublishingModal } from '@/components'

export default {
  data: function() {
    return {
      /**
       * Stores the entry fields configuration object
       *
       * @type  {array}
       */
      fields: [],

      /**
       * An object sent to child components; used to signal that the fields need to be reset, with the ability
       * to limit the reset to only certain fields
       *
       * @type  {object}
       */
      formFieldsReset: {
        /**
         * A list of field names to include in a reset action
         *
         * @type  {array}
         */
        includeFields: [],

        /**
         * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
         * set the fresh value.
         *
         * @type  {integer}
         */
        resetData: null,

        moduleTableName: this.moduleTableName,
      },

      /**
       * Fields copy
       *
       * @type  {Array}
       */
      fieldsCopy: [],

      /**
       * First dialog fired state
       *
       * @type  {boolean}
       */
      checkIsFormDirty: true,

      /**
       * Click counter
       *
       * @type  {integer}
       */
      clickCounter: 0,
    }
  },

  props: {
    editedReferenceId: {
      default: null,
      type: [Number],
    },
    moduleTableName: {
      type: String,
      required: true,
    },
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
    }),

    /**
     * Admin module getter
     *
     * @return  {object}
     */
    admin() {
      const getterName = `${this.vuexModuleName}/${this.vuexModuleName}`

      return store.getters[getterName]
    },

    /**
     * vuexModuleName getter
     *
     * @return  {string}
     */
    vuexModuleName() {
      return `${this.moduleTableName}-reference-${Date.now()}`
    },

    /**
     * UI values getter
     *
     * @return  {object}
     */
    ui() {
      if (!_.has(this.photonModule, 'modules')) {
        return {}
      }

      const module = _.find(this.photonModule.modules, [
        'table_name',
        this.moduleTableName,
      ])

      if (this.editedReferenceId) {
        return {
          icon: module.icon,
          title:
            'Editing: ' +
            _.get(this.admin.editedEntry, 'system.anchor_text', ''),
          subtext: module.name,
        }
      }

      return {
        icon: module.icon,
        title: i18n.t('admin.creatingANewEntry'),
        subtext: module.name,
      }
    },
  },

  /**
   * Define components
   *
   * @type  {Object}
   */
  components: {
    SchedulePublishingModal,
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Executes on referenceSubmitSuccess event emitted from a child entry-form component.
     * Emits the closeReferencesPanel event.
     *
     * @param   {object}  result
     * @return  {void}
     */
    referenceSubmitSuccess(result) {
      this.$emit('closeReferencesPanel', result.entry)
    },

    /**
     * Emits the closeReferencesPanel event
     *
     * @return  {void}
     */
    closeReferencesPanel() {
      if (!this.checkIsFormDirty) {
        this.$emit('closeReferencesPanel', null)
        return
      }

      if (!this.isFormDirty()) {
        this.$emit('closeReferencesPanel', null)
        return
      }

      const answer = window.confirm(i18n.t('admin.changesMadeWarning'))

      if (!answer) {
        return
      }

      this.$emit('closeReferencesPanel', null)
    },

    /**
     * Sets the formFieldsReset data property in order to force refresh of the EntryForm component
     *
     * @param   {object}  options.includeFields The names of field components that need to be included in reset
     * @return  {void}
     */
    resetFormFields({ includeFields = null } = {}) {
      this.formFieldsReset.resetData = moment().valueOf()

      this.formFieldsReset.includeFields = []

      if (includeFields) {
        this.formFieldsReset.includeFields = includeFields
      }

      eventBus.$emit('formFieldsReset', this.formFieldsReset)
    },

    /**
     * Recalculates and applies the CSS offset based on a number of open reference panels
     *
     * @return  {void}
     */
    recalculateCssOffset() {
      const offset = 5 * $(this.$root.$el).find('.photon-reference').length
      const $photonReference = $(this.$el).find('.photon-reference')
      $($photonReference).css({
        left: parseInt($($photonReference).css('left')) + offset,
      })
    },

    /**
     * Bootstraps the reference view
     *
     * @param   {integer}  moduleEntryId
     * @return  {void}
     */
    bootstrap(moduleEntryId = null) {
      store
        .dispatch(`${this.vuexModuleName}/adminBootstrap`, {
          moduleEntryId,
          moduleTableName: this.moduleTableName,
          shouldFetchSearch: false,
        })
        .then(() => {
          this.fields = getEntryFields(this)
          this.resetFormFields()
        })
        .catch(error => {
          pError('Reference view bootstrap action failed with an error', error)
        })
    },

    /**
     * Checks if form data is dirty
     *
     * @return  {Boolean}
     */
    isFormDirty() {
      let isDirty = false

      for (let i = 0; i < this.fieldsCopy.length; i++) {
        const fieldCopy = this.fieldsCopy[i]
        if (
          _.get(this.admin.editedEntry, `fields.${fieldCopy.name}`) !==
          fieldCopy.value
        ) {
          isDirty = true
          break
        }
      }

      return isDirty
    },

    /**
     * Prevents the navigation upon reload callback function
     *
     * @param   {event}  event
     * @return  {void}
     */
    preventNav(event) {
      if (this.isFormDirty()) {
        event.preventDefault()

        event.returnValue = ''
      }
    },

    /**
     * Catches the user interaction with entry form
     *
     * @return  {void}
     */
    onFirstInteract() {
      if (this.clickCounter === 0) {
        this.fieldsCopy = this.fields.map(field => {
          return { ...field }
        })

        this.checkIsFormDirty = true

        this.clickCounter++
      }
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  created: function() {
    if (!_.has(store.state, this.vuexModuleName)) {
      store.registerModule(
        this.vuexModuleName,
        adminFactory(this.vuexModuleName)
      )

      store
        .dispatch(`${this.vuexModuleName}/setIsReference`, {
          value: true,
        })
        .then(() => {
          this.bootstrap(this.editedReferenceId)

          this.$nextTick(() => {
            this.recalculateCssOffset()
          })
        })
    }
  },

  /**
   * Set the beforeMount hook
   *
   * @return  {void}
   */
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav)
  },

  /**
   * Set the beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy: function() {
    window.removeEventListener('beforeunload', this.preventNav)
  },
}
